import { useEffect, useRef, useState } from "react";

import {
  Box,
  Button,
  CircularProgress,
  Typography,
  useTheme
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCloudArrowUp,
  faFolderOpen,
  faCircleCheck,
  faTriangleExclamation,
  faClipboardCheck,
  faCircleInfo,
  faCircleMinus
} from "@fortawesome/free-solid-svg-icons";

import { hexToRgba } from "@aclymatepackages/converters";

import SmartUploadReviewDialog from "./SmartUploadReviewDialog";
import { REVIEWABLE_TASK_STATUSES } from "./reviewLineGates";
import { isReviewDrivenCsvFamily } from "../../../helpers/smartUploadLineRequirements";

import { useAccountData } from "../../../helpers/firebase";
import {
  startSmartUploadBatch,
  subscribeToSmartUploadTask,
  subscribeToSmartUploadTaskData
} from "../../../helpers/smartUpload";

// Accepts PDF, JPG/PNG/HEIC images, CSV, and XLSX at the same level as a
// whole folder or ZIP -- no "advanced" gating (design-intent 3.10). No
// client-side parse or preview happens here: extraction is asynchronous
// (PRD §8.1/§8.2) and this flow's job ends once the batch is created.
const supportedExtensions = [
  "pdf",
  "jpg",
  "jpeg",
  "png",
  "heic",
  "csv",
  "xlsx",
  "zip"
];

const isSupportedFile = ({ name }) => {
  const extension = name.split(".").pop().toLowerCase();
  return supportedExtensions.includes(extension);
};

const UploadDropzone = ({ onFilesSelected, disabled }) => {
  const { palette } = useTheme();
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);

  const openFilePicker = () => fileInputRef.current?.click();
  const openFolderPicker = () => folderInputRef.current?.click();

  const handleFiles = (fileList) => {
    const allFiles = Array.from(fileList || []);
    const files = allFiles.filter(isSupportedFile);
    const unsupportedFileNames = allFiles
      .filter((file) => !isSupportedFile(file))
      .map(({ name }) => name);

    onFilesSelected(files, unsupportedFileNames);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    handleFiles(event.dataTransfer.files);
  };

  const handleInputChange = (event) => handleFiles(event.target.files);

  return (
    <>
      <Box
        onClick={disabled ? undefined : openFilePicker}
        onDragOver={(event) => {
          event.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          p: 5,
          cursor: disabled ? "default" : "pointer",
          border: `2px dashed ${
            isDragging ? palette.primary.main : palette.backgroundGray.dark
          }`,
          borderRadius: 2,
          backgroundColor: isDragging
            ? hexToRgba(palette.primary.main, 0.08)
            : palette.backgroundGray.light,
          opacity: disabled ? 0.6 : 1,
          transition: "all 0.15s ease"
        }}
      >
        <FontAwesomeIcon
          icon={faCloudArrowUp}
          size="2x"
          style={{ color: palette.primary.main }}
        />
        <Typography variant="subtitle1">
          Drag documents here, or click to browse
        </Typography>
        <Typography variant="caption" color="textSecondary">
          Accepts PDF, JPG, PNG, HEIC, CSV, XLSX, or a ZIP of many files
        </Typography>
        <Button
          size="small"
          disabled={disabled}
          startIcon={<FontAwesomeIcon icon={faFolderOpen} />}
          onClick={(event) => {
            event.stopPropagation();
            openFolderPicker();
          }}
        >
          Select an entire folder
        </Button>
      </Box>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        hidden
        onChange={handleInputChange}
      />
      <input
        ref={folderInputRef}
        type="file"
        multiple
        webkitdirectory=""
        hidden
        onChange={handleInputChange}
      />
    </>
  );
};

const ROW_STATUS_ICONS = {
  error: { icon: faTriangleExclamation, colorKey: "error" },
  in_review: { icon: faClipboardCheck, colorKey: "primary" },
  extracted: { icon: faCircleInfo, colorKey: "warning" },
  rejected: { icon: faCircleMinus, colorKey: "backgroundGray", shade: "dark" }
};

const rowStatusIcon = ({ status, palette }) => {
  if (status === "pending" || status === "extracting") {
    return <CircularProgress size={16} />;
  }
  const {
    icon,
    colorKey,
    shade = "main"
  } = ROW_STATUS_ICONS[status] || {
    icon: faCircleCheck,
    colorKey: "secondary"
  };
  return (
    <FontAwesomeIcon icon={icon} style={{ color: palette[colorKey][shade] }} />
  );
};

const TaskDataRow = ({ row }) => {
  const { palette } = useTheme();

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5 }}>
      {rowStatusIcon({ status: row.status, palette })}
      <Box sx={{ flexGrow: 1 }}>
        <Typography variant="body2">{row.fileName}</Typography>
      </Box>
      {row.errorMessage && (
        <Typography variant="caption" color="error">
          {row.errorMessage}
        </Typography>
      )}
    </Box>
  );
};

// entityContext is null from this general surface's own default -- an
// entity-scoped slideout tab (Location/entity allocation, ticket
// 9zFp7uWgIg1zwzgQT8ia's EntitySmartUploadTab.js) reuses this exact
// component, passing its own real entityContext/entityName through, rather
// than duplicating the dropzone + progress-tracking UI in a second file
// (see that ticket's spec, New Files > EntitySmartUploadTab.js: "reusing the
// existing settings CSV-upload tracking UI... does not build a new progress
// component").
const batchStatusCopy = ({ status, entityName, readyFileCount }) => {
  if (status === "uploading") {
    return `Uploading and staging your batch${entityName ? ` for ${entityName}` : ""}…`;
  }
  if (REVIEWABLE_TASK_STATUSES.includes(status)) {
    return `${readyFileCount} ${readyFileCount === 1 ? "file is" : "files are"} ready to review.`;
  }
  if (status === "finished") {
    return "Every file in this batch has been reviewed.";
  }
  return "Your batch is being processed.";
};

const SmartUploadFlow = ({ onClose, entityContext = null, entityName }) => {
  const { palette } = useTheme();
  const [{ id: companyId }] = useAccountData();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [failedUploads, setFailedUploads] = useState([]);
  const [taskDocId, setTaskDocId] = useState(null);
  const [task, setTask] = useState(null);
  const [taskDataRows, setTaskDataRows] = useState([]);
  const [isReviewOpen, setIsReviewOpen] = useState(false);

  useEffect(() => {
    if (!companyId || !taskDocId) {
      return undefined;
    }
    const unsubscribeTask = subscribeToSmartUploadTask({
      companyId,
      taskDocId,
      onUpdate: setTask
    });
    const unsubscribeTaskData = subscribeToSmartUploadTaskData({
      companyId,
      taskDocId,
      onUpdate: setTaskDataRows
    });
    return () => {
      unsubscribeTask();
      unsubscribeTaskData();
    };
  }, [companyId, taskDocId]);

  const handleFilesSelected = async (files, unsupportedFileNames = []) => {
    const unsupportedRejections = unsupportedFileNames.map((fileName) => ({
      fileName,
      reason: "Unsupported file type"
    }));

    if (!files.length) {
      setFailedUploads(unsupportedRejections);
      return;
    }

    setIsUploading(true);
    setUploadError(null);

    const result = await startSmartUploadBatch({
      companyId,
      files,
      entityContext
    });
    setIsUploading(false);
    setFailedUploads([...unsupportedRejections, ...(result.failed || [])]);

    if (result.error) {
      setUploadError(result.error);
      return;
    }
    setTaskDocId(result.taskDocId);
  };

  if (taskDocId) {
    const isReadyForReview = REVIEWABLE_TASK_STATUSES.includes(task?.status);
    // A finished batch is still reachable (#42) -- mirrors
    // AccountActivity.js's own isFinishedSmartUpload: "Open" re-enters the
    // same review dialog, which renders read-only except for the
    // manual-entry CTA on eligible rows.
    const isFinishedSmartUpload =
      task?.status === "finished" && !isReviewDrivenCsvFamily(task?.document_family);
    const readyFileCount = taskDataRows.filter(
      ({ status }) => status === "in_review" || status === "extracted"
    ).length;

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography variant="subtitle1">
          {batchStatusCopy({
            status: task?.status,
            entityName,
            readyFileCount
          })}
        </Typography>
        {!isReadyForReview && task?.status !== "finished" && (
          <Typography variant="caption" color="textSecondary">
            You&apos;ll be emailed when it&apos;s ready to review — feel free
            to close this.
          </Typography>
        )}
        <Box>
          {taskDataRows.map((row) => (
            <TaskDataRow key={row.id} row={row} />
          ))}
        </Box>
        {isReadyForReview && (
          <Button
            variant="contained"
            color="primary"
            sx={{ color: palette.common.white }}
            onClick={() => setIsReviewOpen(true)}
          >
            Review
          </Button>
        )}
        {isFinishedSmartUpload && (
          <Button variant="outlined" onClick={() => setIsReviewOpen(true)}>
            Open
          </Button>
        )}
        <Button onClick={onClose}>Close</Button>
        {isReviewOpen && (
          <SmartUploadReviewDialog
            companyId={companyId}
            taskDocId={taskDocId}
            onClose={() => setIsReviewOpen(false)}
          />
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <UploadDropzone
        onFilesSelected={handleFilesSelected}
        disabled={isUploading}
      />
      {isUploading && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="body2">Uploading…</Typography>
        </Box>
      )}
      {uploadError && (
        <Typography variant="body2" color="error">
          Something went wrong starting this batch — please try again.
        </Typography>
      )}
      {failedUploads.map(({ fileName, reason }) => (
        <Typography key={fileName} variant="caption" color="error">
          {fileName}: {reason}
        </Typography>
      ))}
    </Box>
  );
};

export default SmartUploadFlow;
